import { injectable } from 'inversify';

import { t } from '@vk-hr-tek/core/translations';
import type { FlowTreeNode } from '@vk-hr-tek/ui/FlowTree';

import { Workflow } from '@app/gen/settings';

import { createWorker } from './settings.workflow.factory';

interface Response {
  id: number;
  data: FlowTreeNode;
  error: Error;
}

@injectable()
export class SettingsWorkflowMapper {
  private worker: Worker | null = null;

  private nextActionId = 0;

  private inFlightActions = new Map<
    number,
    { resolve: (value: FlowTreeNode) => void; reject: (value: Error) => void }
  >();

  handleMessage(message: MessageEvent<Response>) {
    const currentAction = this.inFlightActions.get(message.data.id);

    if (!currentAction) {
      return;
    }

    this.inFlightActions.delete(message.data.id);

    if (message.data.error) {
      currentAction.reject(message.data.error);
      return;
    }

    currentAction.resolve(message.data.data);
  }

  async init() {
    if (!this.worker) {
      this.worker = createWorker();

      this.worker.onmessage = this.handleMessage.bind(this);
    }
  }

  async transform(workflow?: Workflow): Promise<FlowTreeNode> {
    await this.init();

    const id = this.nextActionId++;

    let resolve: (value: FlowTreeNode) => void = () => {};
    let reject: (value: Error) => void = () => {};

    const promise: Promise<FlowTreeNode> = new Promise(
      (res: (value: FlowTreeNode) => void, rej) => {
        resolve = res;
        reject = rej;
      },
    );

    this.inFlightActions.set(id, { resolve, reject });
    this.worker?.postMessage({
      id,
      data: JSON.parse(JSON.stringify(workflow)),
      employeeText: t('settings.mappers.workflow.employeeText'),
    });

    return promise;
  }
}
